import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';

import { webhooksEndpoints as endpoints } from '@/api/endpoints';
import { callGet } from '@/api/fetcher';
import { selectAccountSlug } from '@/redux/session/selectors';
import { Webhooks } from '@/shared/types/webhook';

import { useDialogAction } from './useDialogAction';
import { useLeaveModal } from './useLeaveModal';
import { selectBrainId } from '../redux/selectors';

type WebhookLink = { name: string; to: string | null; goTo: (() => void) | null };

type WebhookPageLink = { to: string; goTo: () => void };

export const useWebhookName = (webhookId?: string): string | null => {
  const brainId = useSelector(selectBrainId);

  const { data } = useQuery<Webhooks, Error>({
    queryKey: [endpoints.webhooks(brainId)],
    queryFn: () => callGet(endpoints.webhooks(brainId)),
    enabled: !!brainId && !!webhookId,
  });

  return data?.webhooks?.find((webhook) => webhook.webhook_id === webhookId)?.name ?? null;
};

export const useGoToWebhookPage = (webhookId?: string): WebhookPageLink | null => {
  const leaveModal = useLeaveModal();
  const slug = useSelector(selectAccountSlug);
  const brainId = useSelector(selectBrainId);

  if (!slug || !brainId || !webhookId) {
    return null;
  }

  const to = `/${slug}/ai-agents/${brainId}/workflows/webhooks/${webhookId}`;

  return { to, goTo: () => leaveModal(to) };
};

export const useWebhookLink = (webhookId?: string, actionId?: string): WebhookLink | null => {
  const leaveModal = useLeaveModal();
  const slug = useSelector(selectAccountSlug);
  const brainId = useSelector(selectBrainId);
  const name = useWebhookName(webhookId);
  const action = useDialogAction(actionId);

  if (!name) {
    return null;
  }

  const params =
    action && actionId ? new URLSearchParams({ node: action.node_id, nodeAction: actionId }) : null;
  const to =
    slug && brainId && action?.dialog_id && params
      ? `/${slug}/ai-agents/${brainId}/workflows/dialogs/${action.dialog_id}?${params}`
      : null;

  return { name, to, goTo: to ? () => leaveModal(to) : null };
};
